/**
 * Issuing a credit note (`issue_credit_note` in `domains/finance/services.py`).
 *
 * The only way to correct an issued invoice — the original bill is never touched, its
 * total stands, and the credit note reduces the balance beside it. The reason is
 * mandatory on the server (`FinanceRefused` if blank) because a credit note without one
 * leaves nobody able to say, later, why a client was billed one amount and charged
 * another. It is mandatory here too, before the request is even made.
 */

import { STAFF, qk, useApi, useApiMutation, type CreditNote, type Invoice } from '@legalcrm/api';
import { Form, FormActions, FormInput, FormTextarea, Modal, useZodForm } from '@legalcrm/ui';
import { z } from 'zod';

import { formatMoney } from '@/lib/format';

const schema = z.object({
  amount: z
    .string()
    .trim()
    .min(1, 'Enter an amount')
    .refine((value) => Number(value) > 0, 'Enter an amount greater than zero'),
  reason: z
    .string()
    .trim()
    .min(3, 'A credit note needs a reason. It is part of the permanent record.'),
});

type Values = z.infer<typeof schema>;

export function IssueCreditNoteDialog({
  invoice,
  open,
  onClose,
}: {
  invoice: Invoice;
  open: boolean;
  onClose: () => void;
}) {
  const api = useApi();
  const form = useZodForm(schema, { defaultValues: { amount: '', reason: '' } });

  const issue = useApiMutation<CreditNote, Values>({
    mutationFn: (values) =>
      api.post<CreditNote>(STAFF.invoiceCreditNotes(invoice.id), {
        amount: values.amount,
        reason: values.reason.trim(),
      }),
    invalidates: [qk.detail('invoices', invoice.id), qk.all('invoices')],
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Issue a credit note"
      description={`Against ${invoice.invoice_number}. The bill itself is never changed — this reduces the balance beside it.`}
    >
      <Form
        form={form}
        ariaLabel="Issue a credit note"
        error={issue.error}
        onSubmit={async (values) => {
          await issue.mutateAsync(values);
          form.reset();
          onClose();
        }}
      >
        <p className="rounded border bg-surface-sunken px-3 py-2 text-sm text-ink-muted">
          Currently owed: {formatMoney(invoice.balance, invoice.currency)}
        </p>

        <FormInput
          form={form}
          name="amount"
          label={`Amount (${invoice.currency})`}
          inputMode="decimal"
        />

        <FormTextarea
          form={form}
          name="reason"
          label="Reason"
          rows={3}
          hint="Why the client is being credited. Stays on the record permanently."
        />

        <FormActions submitLabel="Issue credit note" busy={issue.isPending} onCancel={onClose} />
      </Form>
    </Modal>
  );
}
